import PublicLayout from "@/components/PublicLayout";
import JsonLd, { breadcrumb, serviceSchema } from "@/components/JsonLd";
import { Link } from "react-router-dom";
import { HardHat, Hammer, Home as HomeIcon, CheckCircle2, ArrowRight } from "lucide-react";

const SERVICES = [
  {
    key: "new_construction",
    icon: HardHat,
    title: "New Construction",
    description:
      "From dirt to keys — we manage every phase of building your custom home in the Coastal Bend. Engineered for Gulf weather, finished to your taste.",
    image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    bullets: [
      "Custom architectural design or pick a stock plan",
      "Hurricane-rated windows and standing-seam metal roofs",
      "Foundation, framing, MEP, finish — single contractor",
      "Weekly client walkthroughs with photo updates",
    ],
  },
  {
    key: "remodel",
    icon: Hammer,
    title: "Remodels",
    description:
      "Kitchen, primary bath, additions — bring your home into the next decade with finishes and layouts that actually fit how you live.",
    image: "https://images.unsplash.com/photo-1771371282665-545256b20dca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    bullets: [
      "Full-service kitchen and bath remodels",
      "Custom cabinetry & quartz/stone counters",
      "Permits, inspections, and design support handled",
      "Fixed-price contracts — no surprise change orders",
    ],
  },
  {
    key: "renovation",
    icon: HomeIcon,
    title: "Renovations",
    description:
      "Whole-home modernizations, post-storm repairs, and structural upgrades. We bring older Coastal Bend homes back to better than new.",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    bullets: [
      "Roofing, siding, and impact-rated window upgrades",
      "Open-concept layout reconfigurations",
      "Insurance claim coordination after storm damage",
      "Energy-efficient HVAC, insulation, and re-wiring",
    ],
  },
];

export default function Services() {
  return (
    <PublicLayout>
      <JsonLd id="bc-services" data={breadcrumb([{name: "Home", url: "/"}, {name: "Services", url: "/services"}])} />
      {SERVICES.map((s) => <JsonLd key={s.key} id={`svc-${s.key}`} data={serviceSchema(s)} />)}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
        <div className="label-overline mb-4">Services</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
          What we build for the <span style={{color:'var(--accent)'}}>Coastal Bend.</span>
        </h1>
        <p className="text-lg mt-6 max-w-2xl leading-relaxed" style={{color:'var(--text-muted)'}}>
          Three core crafts, one accountable team. Every project is run by the same crew that bid it.
        </p>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 space-y-20">
        {SERVICES.map((s, i) => (
          <section key={s.key} id={s.key} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid={`service-section-${s.key}`}>
            <div className={`lg:col-span-7 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
              <div className="overflow-hidden rounded-2xl aspect-[16/10]">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className={`lg:col-span-5 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
              <div className="w-12 h-12 rounded-md flex items-center justify-center mb-5" style={{background: 'rgba(212,163,115,0.1)'}}>
                <s.icon size={22} style={{color:'var(--accent)'}}/>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-4">{s.title}</h2>
              <p className="text-base leading-relaxed mb-6" style={{color:'var(--text-muted)'}}>{s.description}</p>
              <ul className="space-y-3 mb-8">
                {s.bullets.map((b) => (
                  <li key={b} className="flex gap-3 text-sm">
                    <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{color:'var(--accent)'}}/>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link to={`/contact?service=${s.key}`} className="btn-primary" data-testid={`cta-${s.key}`}>Request a Quote <ArrowRight size={16}/></Link>
            </div>
          </section>
        ))}
      </div>
    </PublicLayout>
  );
}
